import * as React from "react"
import type { Doc } from "../lib/client"
import { laneLabel, parseCsv, statusClass, type DocItem } from "../lib/docs"
import { render, splitTitle } from "../lib/markdown"

/**
 * One document on screen.
 *
 * Three shapes, because three shapes is what the documents actually are — and showing a
 * registry as raw comma-separated text is how a table stops being readable at the exact
 * moment somebody needs to scan it.
 */
interface ReaderProps {
  doc: Doc
  meta: DocItem
  lane?: string | null
  loading?: boolean
  /** Showing the original because this document has no telling in the chosen language. */
  untranslated?: boolean
  language?: string
  original?: string
}

export const Reader: React.FC<ReaderProps> =
({ doc, meta, lane, loading, untranslated, language, original }) => {
  const isMarkdown = /\.md$/i.test(doc.path)
  const csv = /\.csv$/i.test(doc.path) ? parseCsv(doc.text) : null

  const { title, body } = isMarkdown ? splitTitle(doc.text) : { title: null, body: doc.text }
  const html = React.useMemo(() => (isMarkdown ? render(body) : ""), [isMarkdown, body])

  return (
    <article aria-busy={loading}>
      <header className="sf-doc-head">
        <p className="sf-kicker">
          {[lane ? laneLabel(lane) : null, meta.num || folder(doc.path)]
            .filter(Boolean).join(" · ")}
        </p>
        <h1>{title ?? meta.name}</h1>
        <div className="sf-meta">
          <span className="sf-chip">{doc.path}</span>
          <span className="sf-chip"><b>{Math.max(1, Math.round(doc.bytes / 1024))}</b> KB</span>
          {doc.truncated && <span className="sf-chip">shown truncated</span>}
        </div>
      </header>

      {untranslated && (
        <p className="sf-note warn">
          <b>Not translated to {language} yet.</b>
          You are reading the {original} original. Nothing is missing from it — the
          translation is what does not exist.
        </p>
      )}

      {csv ? <Registry head={csv.head} rows={csv.rows} />
        : isMarkdown ? <div className="sf-prose" dangerouslySetInnerHTML={{ __html: html }} />
        : <div className="sf-prose"><pre><code>{doc.text}</code></pre></div>}

      {doc.truncated && (
        <p className="sf-note warn">
          <b>This is not the whole file.</b>
          It is longer than the client's read limit, so the end is missing. The complete
          document is in your repository.
        </p>
      )}
    </article>
  )
}

const Registry: React.FC<{ head: string[]; rows: string[][] }> = ({ head, rows }) => (
  <div className="sf-table-wrap">
    <table>
      <thead>
        <tr>{head.map((h, i) => <th key={i}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((cell, j) => {
              const status = statusClass(cell)
              return (
                <td key={j}>
                  {status ? <span className={`sf-st ${status}`}>{cell}</span> : cell}
                </td>
              )
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

function folder(path: string): string {
  const parts = path.split("/")
  return parts.length > 1 ? parts[parts.length - 2] : "document"
}
